import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Input, Button, Table, Badge } from 'reactstrap';
import { getCustomerReportRequest } from '../../../api/controller/CustomerReportController';
import { Loader } from '../../../custom/components/Loader';

const money = (n) => `MVR ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Customer lookup — the day-to-day counterpart to the customer report.
 *
 * Backed by the same endpoint, so the figures can never disagree between the
 * two screens. Filtering happens server-side; nothing is shown until a search
 * is entered, since the full customer list belongs in the report.
 */
const Customers = () => {

    const [query, setQuery] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [selected, setSelected] = useState(null);

    const search = async () => {
        if (!query.trim()) { return }
        setLoading(true);
        setSelected(null);
        const response = await getCustomerReportRequest({ SearchQuery: query.trim() });
        setRows(response?.Status !== 'error' ? (response?.data ?? []) : []);
        setSearched(true);
        setLoading(false);
    };

    useEffect(() => { document.title = 'Customers'; }, []);

    const detail = (label, value) => (
        <Col md="4" xs="6" className="mb-3">
            <div className="text-muted" style={{ fontSize: '0.8em' }}>{label}</div>
            <div className="fs-5">{value}</div>
        </Col>
    );

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <div className="page-title">
                            <h4 className="mb-3 font-size-18">Customers</h4>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Row className="align-items-center">
                                    <Col md={8} xs={12}>
                                        <Input
                                            placeholder="Search by name, company, email or mobile"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { search() } }}
                                        />
                                    </Col>
                                    <Col md={4} xs={12} className="mt-2 mt-md-0">
                                        <Button color="primary" onClick={search} disabled={loading || !query.trim()}>
                                            <span className="mdi mdi-magnify align-middle me-1"></span>
                                            {loading ? 'Searching…' : 'Search'}
                                        </Button>
                                    </Col>
                                </Row>

                                {loading && <Loader loading />}

                                {searched && !loading && rows.length === 0 && (
                                    <div className="text-muted text-center py-4">No customers matched that search.</div>
                                )}

                                {rows.length > 0 && (
                                    <div className="table-responsive mt-4">
                                        <Table className="table-sm align-middle mb-0" hover>
                                            <thead>
                                                <tr>
                                                    <th>Customer</th>
                                                    <th>Company</th>
                                                    <th className="text-end">Balance</th>
                                                    <th className="text-center">Vessels</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((r) => (
                                                    <tr key={r.customer_id}>
                                                        <td>
                                                            {r.name}
                                                            <div className="text-muted" style={{ fontSize: '0.85em' }}>{r.email}</div>
                                                        </td>
                                                        <td>{r.company_name || <span className="text-muted">—</span>}</td>
                                                        <td className="text-end">{money(r.balance)}</td>
                                                        <td className="text-center">{r.vessel_count}</td>
                                                        <td className="text-end">
                                                            <Button size="sm" color="light" onClick={() => setSelected(r)}>View</Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {selected && (
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody>
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="mb-1">{selected.name}</h5>
                                            <div className="text-muted">
                                                {selected.company_name || 'No company on record'}
                                            </div>
                                        </div>
                                        <Badge color="success" pill>{money(selected.balance)} balance</Badge>
                                    </div>
                                    <Row>
                                        {detail('Email', selected.email || '—')}
                                        {detail('Mobile', selected.mobile_number || '—')}
                                        {detail('Vessels', selected.vessel_count)}
                                        {/* Two different questions: what reached the
                                            wallet, and what the customer paid. The
                                            balance reconciles against the credit,
                                            while the top-up report shows the cash —
                                            showing only one of them read as a
                                            discrepancy between the two screens. */}
                                        {detail('Wallet credit (excl. GST)', money(selected.total_topped_up))}
                                        {detail('GST', money(selected.total_gst))}
                                        {detail('Total paid (incl. GST)', money(selected.total_paid))}
                                        {detail('Total spent', money(selected.total_spent))}
                                        {detail('Last activity', selected.last_activity
                                            ? new Date(selected.last_activity).toLocaleString()
                                            : 'No activity yet')}
                                    </Row>
                                    {selected.vessels?.length > 0 && (
                                        <div className="mb-3">
                                            <div className="text-muted mb-2" style={{ fontSize: '0.85em' }}>
                                                Vessels owned
                                            </div>
                                            <Table size="sm" bordered className="mb-0">
                                                <thead>
                                                    <tr><th>Registration No.</th><th>Vessel</th><th>Status</th></tr>
                                                </thead>
                                                <tbody>
                                                    {selected.vessels.map(v => (
                                                        <tr key={v.id}>
                                                            <td>{v.registration_number}</td>
                                                            <td>{v.name}</td>
                                                            <td>
                                                                <Badge color={{ APPROVED: 'success', PENDING: 'warning', REJECTED: 'danger' }[v.status] ?? 'secondary'}>
                                                                    {v.status}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                    <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                        Totals cover the customer's full history. Balance is current.
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </div>
    );
};

export default Customers;
